import http from './http/index';

export interface ProfilePictureResponse {
    success: boolean;
    message: string;
    data?: {
        profilePicture: string;
        publicUrl: string;
        filename: string;
        originalMetadata?: any;
    };
    error?: string;
}

export interface ProfilePictureInfo {
    profilePicture: string | null;
    publicUrl: string | null;
}

class ProfilePictureAPI {
    private baseUrl = '/admin/profile-picture';

    /**
     * Upload a profile picture
     * @param userId - The user ID (if different from current user, requires admin)
     * @param file - The image file to upload
     * @param isAdminOperation - Whether this is an admin operation for another user
     * @returns Promise with upload response
     */
    async uploadProfilePicture(userId: string, file: File, isAdminOperation: boolean = false): Promise<ProfilePictureResponse> {
        try {
        const formData = new FormData();
        formData.append('profilePicture', file);

            // Use admin route if this is for another user, otherwise use regular route
            const url = isAdminOperation ? `${this.baseUrl}/upload-for-user/${userId}` : `${this.baseUrl}/upload`;
            const response = await http.post<ProfilePictureResponse>(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data;
        } catch (error: any) {
            console.error('Error uploading profile picture:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to upload profile picture',
                error: error.response?.data?.error || error.message,
            };
        }
    }

    /**
     * Remove a profile picture
     * @param userId - The user ID (if different from current user, requires admin)
     * @param isAdminOperation - Whether this is an admin operation for another user
     * @returns Promise with removal response
     */
    async removeProfilePicture(userId: string, isAdminOperation: boolean = false): Promise<{ success: boolean; message: string; error?: string }> {
        try {
            // Use admin route if this is for another user, otherwise use regular route
            const url = isAdminOperation ? `${this.baseUrl}/remove-for-user/${userId}` : `${this.baseUrl}/remove`;
            const response = await http.delete<{ success: boolean; message: string; error?: string }>(url);
            return response.data;
        } catch (error: any) {
            console.error('Error removing profile picture:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to remove profile picture',
                error: error.response?.data?.error || error.message,
            };
        }
    }

    /**
     * Get profile picture information
     * @param userId - The user ID (optional, defaults to current user)
     * @returns Promise with profile picture info
     */
    async getProfilePictureInfo(userId?: string): Promise<{ success: boolean; data?: ProfilePictureInfo; message?: string; error?: string }> {
        try {
            // If userId is provided, use admin route to get another user's picture
            // Otherwise, get current user's picture
            const url = userId ? `${this.baseUrl}/${userId}` : this.baseUrl;
            const response = await http.get<{ success: boolean; data?: ProfilePictureInfo; message?: string; error?: string }>(url);
            return response.data;
        } catch (error: any) {
            console.error('Error getting profile picture info:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to get profile picture info',
                error: error.response?.data?.error || error.message,
            };
        }
    }

    /**
     * Get the full URL for a profile picture
     * @param filePath - The file path from the database
     * @returns The full URL to access the image
     */
    getProfilePictureUrl(filePath: string): string {
        if (!filePath) return '';
        
        // If the filePath is already a full URL (from Digital Ocean Spaces), return it as is
        if (filePath.startsWith('http')) {
            return filePath;
        }
        
        // If using Digital Ocean Spaces, construct the URL directly
        if (process.env.REACT_APP_DO_SPACES_ENDPOINT) {
            const bucketName = process.env.REACT_APP_DO_SPACES_BUCKET || 'profile-pictures';
            const region = process.env.REACT_APP_DO_SPACES_REGION || 'nyc3';
            const key = filePath.includes('profile-pictures/') ? filePath : `profile-pictures/${filePath}`;
            return `https://${bucketName}.${region}.digitaloceanspaces.com/${key}`;
        }
        
        // Fallback to local file system (existing logic)
        // Get the base URL from environment variables following the same pattern as environmentUrls.ts
        let baseURL: string;
        
        if (process.env.REACT_APP_ENV === 'staging') {
            baseURL = 'https://api2.aptiveindex.com';
        } else if (process.env.NODE_ENV === 'production') {
            baseURL = 'https://api.aptiveindex.com';
        } else {
            // Development
            baseURL = 'http://localhost:3001';
        }
        
        return `${baseURL}/uploads/${filePath}`;
    }
}

const profilePictureAPI = new ProfilePictureAPI();
export default profilePictureAPI;